/**
 * Title page editing. The title page uses the same <para> structure as the body
 * but is not loaded into the editor; we edit it directly on the DOM, rewriting
 * ONLY the bookmarked paragraphs' text (Title/Author/Draft/Contact/Copyright)
 * and leaving all other title-page content untouched.
 */
export const TITLE_BOOKMARKS = ["Title", "Author", "Draft", "Contact", "Copyright"] as const;
export type TitleField = (typeof TITLE_BOOKMARKS)[number];

export type TitleValues = Partial<Record<TitleField, string>>;

function titlepageEl(dom: Document): Element | undefined {
  return dom.getElementsByTagName("titlepage")[0];
}

/** Read the current text of each bookmarked title-page paragraph. */
export function readTitlePage(dom: Document): TitleValues {
  const out: TitleValues = {};
  const tp = titlepageEl(dom);
  if (!tp) return out;
  for (const para of Array.from(tp.getElementsByTagName("para"))) {
    const bookmark = para.getAttribute("bookmark") as TitleField | null;
    if (bookmark && (TITLE_BOOKMARKS as readonly string[]).includes(bookmark)) {
      const texts = para.getElementsByTagName("text");
      let text = "";
      for (let i = 0; i < texts.length; i++) text += texts[i]!.textContent ?? "";
      out[bookmark] = text;
    }
  }
  return out;
}

/**
 * Write new text into the given bookmarked paragraphs. Each edited paragraph is
 * reduced to a single <text> run holding the value; its <style> (and any
 * align override) is preserved. Untouched fields and paragraphs are not modified.
 */
export function writeTitlePage(dom: Document, values: TitleValues): void {
  const tp = titlepageEl(dom);
  if (!tp) return;

  for (const para of Array.from(tp.getElementsByTagName("para"))) {
    const bookmark = para.getAttribute("bookmark") as TitleField | null;
    if (!bookmark || !(bookmark in values)) continue;
    const value = values[bookmark];
    if (value === undefined) continue;

    // Remove existing <text> children, keep the <style>.
    for (const text of Array.from(para.getElementsByTagName("text"))) {
      para.removeChild(text);
    }
    const textEl = dom.createElement("text");
    textEl.textContent = value;
    para.appendChild(textEl);
  }
}
